import type { OccupancyCell } from "./OccupancyLayer";

/**
 * Pure math + grouping for the instanced occupancy decks (ADR 197). The JSL
 * warehouse put 15 538 bins on screen and the one-mesh-per-cell layer collapsed
 * to 1 FPS — decks now render as one InstancedMesh per (status × search-state)
 * group: at most 8 draw calls, whatever the bin count.
 *
 * Kept free of three.js/React so the basis math and the group split are
 * unit-tested directly (mirrors palletLoad.ts vs PalletLoadLayer).
 */

/**
 * Affine basis mapping the unit square onto one bin's floor polygon, in the
 * rotated ground group's frame — y is negated exactly like polygonToShape did,
 * so the deck lands where every other floor consumer puts (x, y) meters.
 *
 * Exact for the parallelograms bins are authored as (a 4-point ring maps
 * v0→origin, v0v1→x edge, v0v3→y edge). Any other ring collapses to its
 * bounding box: at 15k instances the deck's job is presence + tint, not the
 * ring's exact silhouette.
 */
export interface DeckBasis {
  ox: number; oy: number;
  xx: number; xy: number;
  yx: number; yy: number;
}

export function deckBasis(polygon: number[][]): DeckBasis | null {
  if (polygon.length < 3) return null;
  if (polygon.length === 4) {
    const [v0, v1, , v3] = polygon;
    const b: DeckBasis = {
      ox: v0[0], oy: -v0[1],
      xx: v1[0] - v0[0], xy: -(v1[1] - v0[1]),
      yx: v3[0] - v0[0], yy: -(v3[1] - v0[1]),
    };
    if (Math.abs(b.xx * b.yy - b.xy * b.yx) > 1e-9) return b;
    // zero-area quad (collinear ring) — fall through to the bbox
  }
  const xs = polygon.map((p) => p[0]);
  const ys = polygon.map((p) => -p[1]);
  const [x0, x1] = [Math.min(...xs), Math.max(...xs)];
  const [y0, y1] = [Math.min(...ys), Math.max(...ys)];
  if (x1 - x0 <= 0 || y1 - y0 <= 0) return null;
  return { ox: x0, oy: y0, xx: x1 - x0, xy: 0, yx: 0, yy: y1 - y0 };
}

/**
 * Row-major Matrix4 elements placing one deck instance: unit square → the bin
 * polygon, at the bin's elevation (local z of the rotated ground group — world
 * height). A bin without the ADR 155 level enrichment stays flat at 0.
 */
export function deckMatrix(item: DeckItem): number[] {
  const b = item.basis;
  return [b.xx, b.yx, 0, b.ox,
          b.xy, b.yy, 0, b.oy,
          0, 0, 1, item.cell.elevationM ?? 0,
          0, 0, 0, 1];
}

/** How a group renders: the plain layer, a search hit, the ADR 196 ghost, an address
 *  an in-progress run has just reached (ADR 324), or a cell a paint-by mode has
 *  muted out of its story (ADR 354). */
/** `origin` (ADR 198 movement focus): the bin a picked label MOVED FROM — the
 *  search isolate can only show where it is now, so the origin is marked
 *  explicitly and outranks match/ghost. */
export type DeckKind = "normal" | "match" | "ghost" | "visited" | "muted" | "origin";

export interface DeckItem { cell: OccupancyCell; basis: DeckBasis; }
export interface DeckGroup {
  key: string;
  status: OccupancyCell["status"];
  kind: DeckKind;
  /** ADR 354 paint-by override; absent = the status palette decides. */
  color?: string;
  items: DeckItem[];
}

/**
 * Split cells into instance groups. No search (`matched` null) → one group per
 * status present. Search active → hits keep their status group ("match"), the
 * rest ghost — still per status, so each group stays a single material.
 *
 * `visited` (ADR 324) marks addresses an in-progress run has reached. **Search outranks
 * it**: a search is something the operator just asked for and ADR 196's isolate is the
 * whole point of it, while live activity is ambient. Encoding both at once would give one
 * deck two meanings and neither would be readable.
 *
 * `paint` (ADR 354) recolors by a per-cell classification instead of scan
 * status: a returned color paints the cell, null mutes it (near-invisible —
 * the mode's story is not about it). Paint colors normal AND match kinds (a
 * search inside a paint mode isolates without losing the ramp); ghosts are
 * already near-invisible and visited keeps its single-color statement.
 */
export function deckGroups(
  items: DeckItem[], matched: Set<string> | null, visited?: ReadonlySet<string> | null,
  paint?: ((cell: OccupancyCell) => string | null) | null, origin?: ReadonlySet<string> | null,
): DeckGroup[] {
  const map = new Map<string, DeckGroup>();
  for (const item of items) {
    let kind: DeckKind = origin?.has(item.cell.address) ? "origin"
      : matched != null
      ? (matched.has(item.cell.address) ? "match" : "ghost")
      : (visited?.has(item.cell.address) ? "visited" : "normal");
    let color: string | undefined;
    if (paint && (kind === "normal" || kind === "match")) {
      const c = paint(item.cell);
      if (c !== null) color = c;
      else if (kind === "normal") kind = "muted";
      // a matched cell outside the paint's story keeps its status color: the
      // operator searched for it, so it must not vanish under the mode.
    }
    const key = `${kind}:${item.cell.status}:${color ?? ""}`;
    let g = map.get(key);
    if (!g) { g = { key, status: item.cell.status, kind, color, items: [] }; map.set(key, g); }
    g.items.push(item);
  }
  return [...map.values()];
}
